import type { MeResponse } from 'shared';

/**
 * What the signed-in actor can actually reach, straight from `/api/me`.
 *
 * Every number here was produced by a query the caller ran through their own
 * RLS-scoped connection — no client-side filtering, and the same queries for every
 * audience. Sign in as Alice and as Bob and the property counts differ because
 * Postgres decided, not because the page asked differently.
 *
 * This is a testing surface for build step 2, not a finished screen. Step 4
 * replaces it with real inventory views.
 */
export function ScopePanel({ me }: { me: MeResponse }) {
  const { actor, scope, balances } = me;

  const rows: { label: string; value: number; note?: string }[] = [
    { label: 'Properties', value: scope.properties },
    { label: 'Units', value: scope.units },
    { label: 'Leases', value: scope.leases },
    { label: 'Tenants', value: scope.tenants },
    { label: 'Open charges', value: scope.openCharges },
    {
      label: 'tenant_sensitive rows',
      value: scope.tenantSensitive,
      note:
        actor.actorType === 'STAFF'
          ? actor.staffRole === 'MAINTENANCE'
            ? 'must be 0 — maintenance has no access to SSNs'
            : 'staff with a business need'
          : 'must be 0 — owners and tenants have no policy on this table',
    },
  ];

  // Invariant 5 made visible: a non-zero count for any portal actor, or for
  // maintenance staff, is a real security regression and should be impossible to
  // miss on the page rather than only in the RLS suite.
  const sensitiveLeak =
    scope.tenantSensitive > 0 && (actor.actorType !== 'STAFF' || actor.staffRole === 'MAINTENANCE');

  return (
    <>
      <h2>What this account can see</h2>
      <p className="muted" style={{ fontSize: '0.85rem' }}>
        Counted by Postgres row-level security, not filtered in the browser.
      </p>

      {sensitiveLeak ? (
        <p className="alert alert--error" role="alert">
          <strong>Invariant 5 violation:</strong> this account can read {scope.tenantSensitive}{' '}
          row(s) from <code>tenant_sensitive</code>. It should read none. Stop and check the
          policies before going further.
        </p>
      ) : null}

      <table className="table">
        <thead>
          <tr>
            <th>Resource</th>
            <th className="num">Visible</th>
            <th>Expectation</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <td>{row.label}</td>
              <td className="num">{row.value}</td>
              <td className="muted">{row.note ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2>Outstanding balances</h2>
      {balances.length === 0 ? (
        <p className="muted">Nothing outstanding that this account can see.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Property</th>
              <th>Unit</th>
              <th className="num">Balance</th>
              <th className="num">Past due</th>
              <th>Age</th>
            </tr>
          </thead>
          <tbody>
            {balances.map((balance) => (
              <tr key={balance.leaseId}>
                <td>{balance.propertyName}</td>
                <td>{balance.unitLabel}</td>
                {/* Decimal strings from the server, rendered verbatim. Never
                    parsed into a JS number (invariant 2). */}
                <td className="num">${balance.totalBalance}</td>
                <td className="num">${balance.pastDueBalance}</td>
                <td>
                  {balance.maxDaysLate > 0 ? (
                    <span className="pill pill--late">{balance.maxDaysLate}d late</span>
                  ) : (
                    <span className="pill">current</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="muted" style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
        Balances are derived from unreversed allocations by the <code>lease_balances</code> view —
        never stored.
      </p>
    </>
  );
}
